import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, CardHeader, CardContent, Table, TableBody, TableRow, TableCell, Avatar, Divider, Button, Typography } from '@mui/material';
import EmployeeDetailsImageCard from './EmployeeDetailsImageCard';
import dateFormatter from '../../../../formatters/dateFormatter';
import EmployeeDetailsChanges from './EmployeeDetailsChanges';
import EmployeeDetailsNegatives from './EmployeeDetailsNegatives';
import EmployeeDetailsNoticeDialog from './EmployeeDetailsNoticeDialog';
import EmployeeDetailsFileDialog from './EmployeeDetailsFileDialog';
import { getSignedFileUrl } from '../../../../actions/utilsActions';

const styles = {
  card: {
    marginBottom: '1.5rem'
  },
  media: {
    height: '25vh',
    cursor: 'pointer'
  },
  avatar: {
    width: '80px',
    height: '80px',
    cursor: 'pointer'
  },
  header: {
    color: '#000',
    backgroundColor: '#FFF',
    textAlign: 'start'
  },
  content: {
    paddingTop: '0'
  },
  actions: {
    mt: '1rem',
    display: 'flex',
    justifyContent: 'space-around'
  }
}

const EmployeeDetailsCard = () => {
  const [openPhoto, setOpenPhoto] = useState(false);
  const [openNotice, setOpenNotice] = useState(false);
  const [openChanges, setOpenChanges] = useState(false);
  const [openNegatives, setOpenNegatives] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photosUrl, setPhotosUrl] = useState([]);
  const [openFile, setOpenFile] = useState(false);

  const employee = useSelector(state => state.employees.current);
  const user = useSelector(state => state.auth.profile);

  const handleOpenPhoto = () => setOpenPhoto(true);
  const handleClosePhoto = () => setOpenPhoto(false);

  const handleOpenNotice = () => setOpenNotice(true);
  const handleCloseNotice = () => setOpenNotice(false);

  const handleOpenChanges = () => setOpenChanges(true);
  const handleCloseChanges = () => setOpenChanges(false);

  const handleOpenNegatives = () => setOpenNegatives(true);
  const handleCloseNegatives = () => setOpenNegatives(false);

  const handleOpenFile = () => setOpenFile(true);
  const handleCloseFile = () => setOpenFile(false);

  const dispatch = useDispatch();

  useEffect(() => {
    fetchSignedUrls();
  }, [employee.photoUrl]);
  
  const fetchSignedUrls = async () => {
    if (employee.photoUrl?.length > 0) {
      try {
        const signedUrls = await Promise.all(
          employee.photoUrl.map(image => 
            dispatch(getSignedFileUrl({ query: image })).unwrap()
          )
        );
        setPhotosUrl(signedUrls);
        
        if (signedUrls.length > 0) {
          setPhotoUrl(signedUrls[0]);
        }
      } catch (error) {
        setPhotosUrl([]);
        setPhotoUrl(null);
      }
    } else {
      setPhotosUrl([]);
      setPhotoUrl(null);
    }
  };

  return (
    <>
      <Card sx={styles.card}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <CardHeader sx={styles.header}
            avatar={<Avatar crossOrigin="" sx={styles.avatar} alt="foto" src={photoUrl} onClick={handleOpenPhoto} />}
            title={`${employee.name} ${employee.firstLastName} ${employee.secondLastName}`}
            subheader={employee.identityNumber}
          />
          {user?.companyRef?.internal ? <Button variant="text" sx={{ mr: '1rem' }} onClick={handleOpenFile}>Archivo</Button> : null}
        </Box>
        <CardContent sx={styles.content}>
          <Typography sx={{ mb: '1rem' }} variant="h6">Datos de contacto</Typography>
          <Divider />
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>Dirección</TableCell>
                <TableCell>{employee.address ?? 'Dirección'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Ciudad</TableCell>
                <TableCell>{employee.city ?? 'Ciudad'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Teléfono fijo</TableCell>
                <TableCell>{employee.fixedPhoneNumber ?? ''}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Teléfono móvil</TableCell>
                <TableCell>{employee.mobilePhoneNumber ?? ''}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Creado en</TableCell>
                <TableCell>{dateFormatter(employee.createdAt ?? null)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tratamiento de datos</TableCell>
                <TableCell><Button variant="text" onClick={handleOpenNotice}>Acuerdos</Button></TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Box sx={styles.actions}>
            <Button variant="text" onClick={handleOpenNegatives}>Eventos adversos</Button>
            <Button variant="text" onClick={handleOpenChanges}>Cambios</Button>
          </Box>
        </CardContent>
      </Card>
      <EmployeeDetailsImageCard id={employee?._id} photos={photosUrl} open={openPhoto} onClose={handleClosePhoto} />
      <EmployeeDetailsNoticeDialog id={employee?._id} notices={employee.noticeUrl ?? []} open={openNotice} onClose={handleCloseNotice} />
      <EmployeeDetailsFileDialog open={openFile} onClose={handleCloseFile} />
      <EmployeeDetailsChanges open={openChanges} onClose={handleCloseChanges} name={employee.name} />
      <EmployeeDetailsNegatives id={employee?._id} open={openNegatives} onClose={handleCloseNegatives} name={employee.name} />
    </>
  );
}

export default EmployeeDetailsCard;
